import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Row, Col, Card, Table, Spin, Empty } from "antd";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import quotationApis from "../../../../api/master/quotations";

const formatINR = (val: number) => {
  if (!isFinite(val)) return "0";
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val.toFixed(0);
};

const formatPrice = (val: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(val);

interface PriceLine {
  rfqId: string;
  supplierQuoteId: number;
  quotationId: number;
  quotedPrice: string | number;
  sellingPrice: string | number | null;
  discount: string | number | null;
  quantity: string | number | null;
  quotedDate: string;
}

const PurchaseSummaryDrawer: React.FC<any> = ({
  paramsData,
  visible,
  onClose,
}) => {
  const supplierName = paramsData?.supplierName || "";
  const materialName = paramsData?.materialName;
  const supplierId = paramsData?.supplierId;
  const materialId = paramsData?.materialId;
  const quotationId = paramsData?.quotationId;

  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState<PriceLine[]>([]);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    if (!visible) {
      setLines([]);
      setChartsReady(false);
      return;
    }
    if (!quotationId || !supplierId || !materialId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res: any = await quotationApis.fetchPurchaseHistoryData({
          quotationId,
          supplierId,
          materialId,
          pageSize: 100,
        });
        if (cancelled) return;
        if (res?.status) {
          setLines(res?.response?.lines || []);
        } else {
          setLines([]);
        }
      } catch {
        if (!cancelled) setLines([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, quotationId, supplierId, materialId]);

  const sortedLines = useMemo(
    () =>
      [...lines].sort(
        (a, b) =>
          new Date(a.quotedDate).getTime() - new Date(b.quotedDate).getTime(),
      ),
    [lines],
  );

  // Chart shows only the latest 12 records (most recent by date),
  // displayed oldest -> newest on the X axis.
  const chartLines = useMemo(
    () => sortedLines.slice(-12),
    [sortedLines],
  );

  const stats = useMemo(() => {
    if (!lines.length) {
      return {
        count: 0,
        latest: 0,
        lowest: 0,
        highest: 0,
        avg: 0,
        variance: 0,
        variancePct: 0,
      };
    }
    const prices = lines.map((l) => Number(l.quotedPrice) || 0);
    const sorted = [...lines].sort(
      (a, b) =>
        new Date(b.quotedDate).getTime() - new Date(a.quotedDate).getTime(),
    );
    const latest = Number(sorted[0]?.quotedPrice) || 0;
    const lowest = Math.min(...prices);
    const highest = Math.max(...prices);
    const avg = prices.reduce((s, n) => s + n, 0) / prices.length;
    const variance = highest - lowest;
    const variancePct = lowest > 0 ? (variance / lowest) * 100 : 0;
    return {
      count: lines.length,
      latest,
      lowest,
      highest,
      avg,
      variance,
      variancePct,
    };
  }, [lines]);

  const priceTrendOption = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          const p = params[0];
          return `<strong>${p.axisValue}</strong><br/>${p.marker} Quoted Price: ${formatPrice(p.value)}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "12%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartLines.map((l) => dayjs(l.quotedDate).format("DD MMM YYYY")),
        axisLabel: { fontSize: 10, rotate: chartLines.length > 6 ? 30 : 0 },
      },
      yAxis: {
        type: "value",
        name: "Price (INR)",
        nameTextStyle: { fontSize: 10, padding: [0, 0, 0, 40] },
        axisLabel: { formatter: (v: number) => formatINR(v), fontSize: 10 },
      },
      series: [
        {
          name: "Quoted Price",
          type: "line",
          data: chartLines.map((l) => Number(l.quotedPrice) || 0),
          itemStyle: { color: "#5470c6" },
          lineStyle: { width: 2 },
          symbolSize: 7,
          smooth: true,
          areaStyle: { opacity: 0.08 },
        },
      ],
    }),
    [chartLines],
  );

  const tableColumns = [
    {
      title: "RFQ / Quotation",
      dataIndex: "rfqId",
      key: "rfqId",
      render: (t: string) => <span style={{ fontWeight: 500 }}>{t}</span>,
    },
    {
      title: "Quoted Date",
      dataIndex: "quotedDate",
      key: "quotedDate",
      render: (v: string) => (v ? dayjs(v).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
      render: (v: string | number | null) => {
        const n = Number(v);
        return v == null || v === "" || !isFinite(n) ? "—" : n.toLocaleString();
      },
    },
    {
      title: "Quoted Price",
      dataIndex: "quotedPrice",
      key: "quotedPrice",
      align: "right" as const,
      render: (v: string | number) => formatPrice(Number(v) || 0),
    },
  ];

  const StatBox = ({
    label,
    value,
    color,
  }: {
    label: string;
    value: React.ReactNode;
    color?: string;
  }) => (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#8094ae" }}>{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: color || "#364a63",
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <Drawer
      destroyOnClose
      title={
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            Purchase History
          </span>
          <span style={{ fontSize: 12, color: "#8094ae", fontWeight: 400 }}>
            {supplierName}
            {materialName ? ` — ${materialName}` : ""}
          </span>
        </div>
      }
      open={visible}
      onClose={onClose}
      afterOpenChange={(open) => setChartsReady(open)}
      width={760}
      bodyStyle={{ padding: 16, background: "#f5f6fa" }}
    >
      <Spin spinning={loading}>
        <Card size="small" bodyStyle={{ padding: 12 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 8,
              color: "#364a63",
            }}
          >
            Purchase Summary
          </div>

          {!loading && lines.length === 0 ? (
            <Empty
              description="No price history available"
              style={{ padding: "24px 0" }}
            />
          ) : (
            <>
              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={12} sm={8} md={4}>
                  <StatBox label="Total Quotes" value={stats.count} />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <StatBox
                    label="Latest Price"
                    value={formatPrice(stats.latest)}
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <StatBox
                    label="Lowest Price"
                    value={formatPrice(stats.lowest)}
                    color="#52c41a"
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <StatBox
                    label="Highest Price"
                    value={formatPrice(stats.highest)}
                    color="#ff4d4f"
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <StatBox label="Avg Price" value={formatPrice(stats.avg)} />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <StatBox
                    label="Variance"
                    value={
                      <span>
                        {formatPrice(stats.variance)}
                        {stats.variancePct > 0 && (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#8094ae",
                              marginLeft: 4,
                              fontWeight: 500,
                            }}
                          >
                            ({stats.variancePct.toFixed(1)}%)
                          </span>
                        )}
                      </span>
                    }
                    color="#faad14"
                  />
                </Col>
              </Row>

              {chartsReady && chartLines.length > 0 && (
                <ReactECharts
                  option={priceTrendOption}
                  style={{ height: 260, width: "100%" }}
                  notMerge
                />
              )}

              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "#526484",
                  }}
                >
                  Quote History
                </div>
                <Table
                  columns={tableColumns}
                  dataSource={lines}
                  rowKey={(r) => `${r.supplierQuoteId}-${r.quotationId}`}
                  size="small"
                  pagination={{ pageSize: 100, hideOnSinglePage: true }}
                />
              </div>
            </>
          )}
        </Card>
      </Spin>
    </Drawer>
  );
};

export default PurchaseSummaryDrawer;
